import { Helmet } from 'react-helmet-async'
import { BreadcrumbsJsonLd } from '@/components/seo/Breadcrumbs'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowRight, CalendarPlus, ArrowLeft } from 'lucide-react'
import { trackEvent } from '@/lib/analytics'
import { SimpleLocationsSection } from '@/components/sections/simple-locations-section'
import { TableOfContents } from '@/components/sections/table-of-contents'

const tocItems = [
  { id: 'introduction-section', title: 'Stress, Sleep & Digestive Balance' },
  { id: 'who-this-is-for', title: 'Who This Is For' },
  { id: 'common-reasons', title: 'Common Reasons People Seek Support' },
  { id: 'how-acupuncture-helps', title: 'How Acupuncture & Chinese Medicine May Help' },
  { 
    id: 'stress-nervous-system', 
    title: 'Stress & the Nervous System',
    children: [
      { id: 'sleep-section', title: 'Sleep' },
      { id: 'digestion-section', title: 'Digestion' },
    ]
  },
  { id: 'what-to-expect', title: 'What to Expect' },
]

export default function StressSleepDigestionPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const showBackLink = location.state?.fromParent === true
  const showHomepageBackLink = location.state?.fromHomepage === true
  const showAppointmentsBackLink = location.state?.fromAppointmentsPage === true

  const openBooking = (source?: string) => {
    if (source) {
      trackEvent('book_appointment_click', { location: source })
    }
    if ((window as any).openBookingModal) {
      ;(window as any).openBookingModal(source)
    }
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Stress, Sleep & Digestive Balance | Raintree Chinese Medicine</title>
        <meta name="description" content="Stress, sleep and digestive balance support at Raintree Chinese Medicine. Care for the nervous system and digestion." />
        <meta property="og:title" content="Stress, Sleep & Digestive Balance | Raintree Chinese Medicine" />
        <meta property="og:description" content="Stress, sleep and digestive balance support at Raintree Chinese Medicine. Care for the nervous system and digestion." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://raintreechinesemedicine.com.au/what-we-help-with/stress-sleep-digestion" />
      </Helmet>
      <h1 className="sr-only">Stress, Sleep & Digestive Balance</h1>
      <BreadcrumbsJsonLd />
      
      {/* Table of Contents */}
      <TableOfContents items={tocItems} />

      {/* Hero Section */}
      <section className="bg-[rgb(132,137,116)] py-16 lg:py-40 px-4 lg:px-0 relative">
        {showHomepageBackLink && (
          <button
            onClick={() => navigate('/#what-we-help-with-section')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to What We Help With
          </button>
        )}
        {showAppointmentsBackLink && !showHomepageBackLink && (
          <button
            onClick={() => navigate('/appointments#areas-of-focus-section')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to Appointments and Fees
          </button>
        )}
        {showBackLink && !showHomepageBackLink && !showAppointmentsBackLink && (
          <button
            onClick={() => navigate('/what-we-help-with')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to What We Help With
          </button>
        )}
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h1 className="text-2xl md:text-3xl mb-4 text-white">
                Stress, Sleep & Digestive Balance
              </h1>
              <p className="text-white/90 sm:w-2/3 mx-auto mb-6 sm:mb-12 text-sm sm:text-base">
                Care for the nervous system and digestion.
              </p>
              <div className="flex flex-col gap-3 mb-4 sm:w-1/2 mx-auto">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => navigate('/appointments')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Appointment Types & Fees
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('stress_hero_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book an Initial Consultation & Treatment
                  </span>
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full border-white text-white hover:bg-white/10 sm:w-1/2 mx-auto"
                onClick={() => openBooking('stress_hero_free_consult')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Book a free 15-minute phone consult
                </span>
              </Button>
            </div>

            {/* Desktop: Side-by-side layout */}
            <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              <div>
                <h1 className="text-3xl lg:text-4xl mb-4 text-white">
                  Stress, Sleep & Digestive Balance
                </h1>
                <p className="text-white/90 text-base lg:text-lg">
                  Underneath many symptoms there is a nervous system and a digestive system that need attention.
                </p>
              </div>
              <div className="flex flex-col gap-3 mx-auto px-4">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => navigate('/appointments')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Appointment Types & Fees
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('stress_hero_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book an Initial Consultation & Treatment
                  </span>
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-white text-white hover:bg-white/80"
                  onClick={() => openBooking('stress_hero_free_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book a free 15-minute phone consult
                  </span>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section id="introduction-section" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Stress, Sleep & Digestive Balance
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                Stress, sleep and digestion sit underneath almost everything else.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                This page is for people who are:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Feeling constantly "on", wired or close to burnout</li>
                <li>Struggling with sleep – trouble falling asleep, staying asleep, or waking unrefreshed</li>
                <li>Experiencing digestive symptoms that flare when life is stressful</li>
                <li>Not sure where to start, but knowing that their nervous system and digestion need support</li>
              </ul>
              <p className="text-gray-600 text-left">
                Raintree offers acupuncture and Chinese herbal medicine aimed at supporting the nervous system, sleep and digestion, alongside any care you are receiving from your GP or other health practitioners.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section id="who-this-is-for" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Who This Is For
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                People often come for stress, sleep and digestive support when they:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Feel tired but wired – exhausted, yet unable to properly rest</li>
                <li>Have responsibilities that feel relentless (caring, parenting, demanding work, business)</li>
                <li>Notice that stress strongly affects their digestion or bowels</li>
                <li>Experience restless, shallow or broken sleep, often waking around the same time each night</li>
                <li>Feel anxious, overwhelmed, flat or "not like themselves"</li>
                <li>Have had blood tests or scans that come back "normal", but still don't feel well</li>
              </ul>
              <p className="text-gray-600 text-left">
                You don't need a specific diagnosis to seek help here. It's also okay if you are already under the care of your GP, psychologist or psychiatrist – treatment can be planned to sit alongside that support.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Common Reasons People Seek Support Section */}
      <section id="common-reasons" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Common Reasons People Seek Support
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                While each person's situation is different, some of the common patterns Renee sees include:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Difficulty winding down at night, lying awake with a racing mind</li>
                <li>Waking multiple times overnight or very early in the morning, and struggling to get back to sleep</li>
                <li>Ongoing feelings of anxiety, agitation or internal restlessness</li>
                <li>A general sense of burnout, numbness, or "running on empty"</li>
                <li>Stress-related digestive issues, such as bloating, nausea, reflux, loose stools or constipation</li>
                <li>IBS-type symptoms that worsen during busy or emotionally demanding times</li>
                <li>Appetite changes – eating very little, grazing all day, or relying on sugar/caffeine to get through</li>
              </ul>
              <p className="text-gray-600 text-left">
                It is very common for these patterns to overlap with hormonal symptoms, pain or fertility concerns. You don't have to neatly separate them for treatment to begin.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How Acupuncture & Chinese Medicine May Help Section */}
      <section id="how-acupuncture-helps" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              How Acupuncture & Chinese Medicine May Help
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                Within this pillar, acupuncture and Chinese medicine are used to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Support your nervous system to move out of constant "fight/flight" mode</li>
                <li>Encourage more regular, restorative sleep patterns over time</li>
                <li>Work with digestive function – appetite, comfort, bowel habits and bloating</li>
                <li>Address the way stress shows up physically (tension, headaches, gut symptoms) as well as emotionally</li>
                <li>Provide a calm, structured space where you can pause and reset</li>
              </ul>
              <p className="text-gray-600 text-left">
                Treatment is tailored to your situation and is always intended to sit <strong>alongside</strong>, not replace, the care you receive from your GP, psychologist, psychiatrist, dietitian or other health professionals.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stress & the Nervous System Section */}
      <section id="stress-nervous-system" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Stress & the Nervous System
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                Modern life asks a lot of the nervous system. Many people feel like they are constantly switching between:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Doing and thinking</li>
                <li>Responding to others' needs</li>
                <li>Managing work, family and health</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                Over time this can show up as:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Feeling "tired and wired" – exhausted but unable to relax</li>
                <li>Feeling flat, numb or detached</li>
                <li>Difficulty concentrating or making decisions</li>
                <li>Heightened responses to everyday stressors</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                Acupuncture sessions can offer:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Time where you are not responsible for anyone else</li>
                <li>A predictable routine of rest and regulation</li>
                <li>Gentle support for your body's own calming pathways</li>
              </ul>
              <p className="text-gray-600 text-left">
                This does not replace psychological therapy, but can complement it by supporting the physical and nervous-system side of stress.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sleep Section */}
      <section id="sleep-section" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Sleep
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                Sleep is often one of the first things to be affected by stress – and one of the hardest things to repair when you are already tired.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                People come with patterns such as:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Difficulty falling asleep, lying awake for long periods</li>
                <li>Waking during the night and feeling "switched on" or anxious</li>
                <li>Waking too early and being unable to return to sleep</li>
                <li>Unrefreshing sleep, with fatigue that doesn't shift even after a full night in bed</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                Within this pillar, treatment may include:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Identifying patterns in your sleep over the week or month</li>
                <li>Using acupuncture with the aim of supporting more stable sleep-wake rhythms</li>
                <li>Working with digestion, evening routines and other lifestyle factors where realistic</li>
                <li>Adjusting treatment over time as your sleep patterns change</li>
              </ul>
              <p className="text-gray-600 text-left">
                If you are already under the care of your GP or another practitioner for sleep (for example, medication or sleep studies), this can be taken into account when planning treatment.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Digestion Section */}
      <section id="digestion-section" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Digestion
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                The digestive system is often closely tied to mood and stress.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                People seek support for issues such as:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Bloating, abdominal discomfort or nausea</li>
                <li>Loose stools, urgency or constipation</li>
                <li>IBS-type patterns where symptoms worsen during stressful periods</li>
                <li>Appetite changes – eating too little, too quickly, or in a way that leaves them feeling unwell</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                Treatment in this area may involve:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Looking at timing, quality and impact of symptoms over time</li>
                <li>Using acupuncture and, where appropriate, Chinese herbal medicine to support digestive function</li>
                <li>Making gradual, realistic changes to eating patterns or routines (without rigid rules or "perfect" diets)</li>
                <li>Considering how stress, sleep and hormones may be influencing your digestion</li>
              </ul>
              <p className="text-gray-600 text-left">
                If you have had recent tests or investigations (for example, with a GP or gastroenterologist), you're welcome to bring those results so they can be considered within the broader picture.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section id="what-to-expect" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              What to Expect in a Stress, Sleep & Digestion Appointment
            </h2>
            
            {/* Your First Visit */}
            <div className="mb-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Your First Visit
                </h3>
                <p className="text-gray-600 mb-4 text-left font-medium">
                  Your initial consultation and treatment is a longer appointment. Together you will:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Talk through your main concerns, including how long things have been this way</li>
                  <li>Map out your stress levels, sleep patterns, digestion and energy across a typical day or week</li>
                  <li>Discuss any other health conditions, medications or supports you are currently using</li>
                  <li>Clarify what feels realistic for you in terms of changes and appointment frequency</li>
                  <li>Begin acupuncture treatment, and, where appropriate, discuss Chinese herbal medicine and gentle, manageable self-care strategies</li>
                </ul>
                <p className="text-gray-600 text-left">
                  You will not be asked to overhaul your lifestyle overnight. The aim is to understand your situation clearly and agree on some next steps that feel possible.
                </p>
              </div>
            </div>

            {/* Ongoing Care */}
            <div>
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Ongoing Care
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  Follow-up appointments focus on:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Checking in on changes in stress, sleep and digestion since the last visit</li>
                  <li>Adjusting acupuncture, any herbs and lifestyle suggestions based on how your system is responding</li>
                  <li>Reviewing how often it makes sense to come in – this may change over time</li>
                  <li>Remaining flexible during busy or intense periods, so treatment supports you rather than becoming another strain</li>
                </ul>
                <p className="text-gray-600 text-left">
                  Plans are collaborative. You and Renee will decide together what feels sustainable and appropriate.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Emotional Support & Other Therapies Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Emotional Support & Other Therapies
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                Stress, sleep and digestive concerns are often linked to life events, responsibilities and past experiences.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                People in this pillar may already be:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Seeing a psychologist, counsellor or psychiatrist</li>
                <li>Taking medication for mood, anxiety or sleep</li>
                <li>Attending other therapies or support groups</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                Acupuncture and Chinese medicine can sit alongside these supports by working with:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>The physical effects of stress on the body</li>
                <li>The nervous system's ability to rest and repair</li>
                <li>The feeling of being held and cared for in a calm environment</li>
              </ul>
              <p className="text-gray-600 text-left">
                If something arises in treatment that needs dedicated psychological support, Renee may encourage you to speak with your existing therapist, GP or another qualified professional.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Is This Approach Right for You? Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Is This Approach Right for You?
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                Stress, sleep and digestive support at Raintree may be a good fit if you:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left">
                <li>Want a space to slow down, be listened to and be treated as a whole person</li>
                <li>Are looking for gentle, step-by-step support rather than quick fixes</li>
                <li>Are open to seeing how stress, sleep, digestion and hormones interact</li>
                <li>Prefer care that works with your existing medical and psychological support, not instead of it</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                If you're unsure whether this is the right fit, you don't have to decide immediately.
              </p>
              <p className="text-gray-600 text-left">
                You're welcome to book a{' '}
                <button
                  onClick={() => openBooking('stress_approach_free_consult')}
                  className="underline font-normal text-gray-600 hover:text-gray-800 cursor-pointer"
                >
                  free 15-minute phone consult
                </button>
                {' '}to talk briefly with Renee about what you're experiencing and ask any initial questions.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Appointments, Locations & How to Start */}
      <SimpleLocationsSection onBook={(locationName) => {
        trackEvent('book_appointment_click', { location: `stress_locations_${locationName.toLowerCase()}` })
        openBooking(`stress_locations_${locationName.toLowerCase()}`)
      }} />

      {/* Next Steps CTA Section */}
      <section className="py-16 lg:py-32 bg-white px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h2 className="text-2xl md:text-xl mb-10 text-[#3f4141]">
                Next steps
              </h2>
              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  For new patients in this area, the best starting point is:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 inline-block text-left max-w-lg mx-auto">
                  <li>
                    <strong>Initial Consultation & Treatment</strong> – a longer first appointment to take a thorough history, understand your patterns and begin treatment.
                  </li>
                </ul>
                <p className="text-gray-600 max-w-lg mx-auto">
                  You can then decide together how often to continue and which clinic is most practical for you.
                </p>
              </div>
              <div className="flex flex-col gap-3 mb-4 sm:w-1/2 mx-auto">
                <Button
                  className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                  onClick={() => {
                    trackEvent('book_appointment_click', { location: 'stress_cta_view_appointments' })
                    navigate('/appointments')
                  }}
                >
                  <span className="inline-flex items-center gap-2">
                    Appointment Types & Fees
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Button>
                <Button
                  className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                  onClick={() => openBooking('stress_cta_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Initial Consult & Treatment
                  </span>
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full border-gray-600 text-gray-600 hover:bg-gray-50 sm:w-1/2 mx-auto"
                onClick={() => openBooking('stress_cta_free_consult')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Free 15-minute phone consult
                </span>
              </Button>
            </div>

            {/* Desktop: Title centered above two columns */}
            <div className="hidden lg:block">
              <h2 className="text-3xl lg:text-4xl mb-16 text-[#3f4141] text-center">
                Next steps
              </h2>
              <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
                <div>
                  <p className="text-gray-600 mb-4 text-base lg:text-md">
                    For new patients in this area, the best starting point is:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 text-base lg:text-md">
                    <li>
                      <strong>Initial Consultation & Treatment</strong> – a longer first appointment to take a thorough history, understand your patterns and begin treatment.
                    </li>
                  </ul>
                  <p className="text-gray-600 text-base lg:text-md">
                    You can then decide together how often to continue and which clinic is most practical for you.
                  </p>
                </div>
                <div className="flex flex-col gap-3 mx-auto px-4 w-80">
                  <Button 
                    className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                    onClick={() => {
                      trackEvent('book_appointment_click', { location: 'stress_cta_view_appointments' })
                      navigate('/appointments')
                    }}
                  >
                    <span className="inline-flex items-center gap-2">
                      Appointment Types & Fees
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Button>
                  <Button 
                    className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                    onClick={() => openBooking('stress_cta_initial_consult')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      Book Initial Consult & Treatment
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-gray-600 text-gray-600 hover:bg-gray-50"
                    onClick={() => openBooking('stress_cta_free_consult')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      Free 15-minute phone consult
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

    </main>
  )
}

